import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { collectionsApi } from '@wtf/shared/api/collections'
import * as CollectionTypes from '@wtf/shared'
import {
    normalizeCollection,
    NormalizedCollection,
} from '@wtf/shared'
import { NormalizedProfileDto } from '@wtf/shared'
import { useCurrentCollectionStore } from '@/stores/currentCollection'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { usePostHog } from 'posthog-js/react'
import { AnalyticsEvents } from '@/utils/analytics'

/**
 * Hook to fetch archived collections
 */
export function useArchivedCollections(
    params: { limit?: number; offset?: number } = {}
) {
    return useQuery({
        queryKey: ['collections-archived', params],
        queryFn: () => collectionsApi.getArchivedCollections(params),
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}

/**
 * Hook to fetch a collection by ID
 * Returns normalized collection data with productIds and lookIds instead of full product/look objects
 * @param id - Collection ID
 * @param options - Optional query options
 * @param options.refetchOnMount - Whether to refetch on mount. Defaults to false since profile/feed caches collections.
 */
export function useCollection(
    id: string | undefined,
    options?: { refetchOnMount?: boolean | 'always' }
) {
    const queryClient = useQueryClient()

    return useQuery({
        queryKey: ['collection', id],
        queryFn: async () => {
            const collectionData = await collectionsApi.getCollectionById(id!)

            // Normalize the collection data and store products and looks in their respective caches
            const normalizedCollection = normalizeCollection(
                queryClient,
                collectionData
            )

            // Store the normalized version in the cache for future use
            queryClient.setQueryData(['collection', id], normalizedCollection)

            return normalizedCollection
        },
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 4 * 60 * 60 * 1000, // 4 hours - prevent GC during extended away periods
        refetchOnMount: options?.refetchOnMount ?? false, // Default false - profile/feed caches collections
        refetchOnWindowFocus: false, // Never refetch on window focus
    })
}

/**
 * Hook to fetch paginated products for a collection
 * Used for lazy loading products when viewing collection details
 * Each page of products is cached individually for efficient re-use
 * 
 * @param collectionId The collection ID to fetch products from
 * @param options.initialOffset Starting offset (default: 0)
 * @param options.pageSize Number of products per page (default: 50)
 * @param options.enabled Whether to enable the query (default: true)
 */
export function useCollectionProducts(
    collectionId: string | undefined,
    options: {
        initialOffset?: number
        pageSize?: number
        enabled?: boolean
    } = {}
) {
    const queryClient = useQueryClient()
    const { initialOffset = 0, pageSize = 50, enabled = true } = options

    return useInfiniteQuery({
        queryKey: ['collection', collectionId, 'products', { pageSize }],
        queryFn: async ({ pageParam = initialOffset }) => {
            if (!collectionId) {
                throw new Error('Collection ID is required')
            }

            const response = await collectionsApi.getCollectionProducts(
                collectionId,
                {
                    limit: pageSize,
                    offset: pageParam,
                }
            )

            // Cache each product individually for reuse across components
            response.products.forEach((product) => {
                queryClient.setQueryData(['product', product.id], product)
            })

            return response
        },
        initialPageParam: initialOffset,
        getNextPageParam: (lastPage) => {
            if (!lastPage.pagination.hasMore) {
                return undefined
            }
            return lastPage.pagination.offset + lastPage.pagination.limit
        },
        enabled: enabled && !!collectionId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}

/**
 * Hook to create a new collection
 */
export function useCreateCollection() {
    const queryClient = useQueryClient()
    const { setCurrentCollection } = useCurrentCollectionStore()
    const { user, isAuthenticated } = useCurrentUser()

    return useMutation({
        mutationFn: (data: CollectionTypes.CreateCollectionRequest) =>
            collectionsApi.createCollection(data),
        onSuccess: (createdCollection: CollectionTypes.CollectionDto) => {
            // Normalize the created collection and store in cache
            const normalizedCollection = normalizeCollection(
                queryClient,
                createdCollection
            )
            queryClient.setQueryData(
                ['collection', createdCollection.id],
                normalizedCollection
            )

            // Automatically set newly created collection as current
            if (
                isAuthenticated &&
                user &&
                createdCollection.ownerId === user.uid
            ) {
                setCurrentCollection(createdCollection.id)
            }

            // Invalidate the owner's profile so their collections list refreshes
            queryClient.invalidateQueries({
                queryKey: ['user', 'profile', createdCollection.ownerId],
            })
            // Invalidate the owner's collections list so Add-to-Collection lists update
            queryClient.invalidateQueries({
                queryKey: ['user', createdCollection.ownerId, 'collections'],
            })
        },
    })
}

/**
 * Hook to update a collection
 */
export function useUpdateCollection() {
    const queryClient = useQueryClient()
    const { setCurrentCollection } = useCurrentCollectionStore()
    const { user, isAuthenticated } = useCurrentUser()

    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: CollectionTypes.UpdateCollectionRequest
        }) => collectionsApi.updateCollection(id, data),
        // Optimistically update the collection cache with new productIds and lookIds
        onMutate: async ({ id, data }) => {
            await queryClient.cancelQueries({ queryKey: ['collection', id] })
            const previous = queryClient.getQueryData<NormalizedCollection>([
                'collection',
                id,
            ])
            if (previous) {
                queryClient.setQueryData<NormalizedCollection>(
                    ['collection', id],
                    {
                        ...previous,
                        productIds: data.productIds ?? previous.productIds,
                        lookIds: data.lookIds ?? previous.lookIds,
                    }
                )
            }
            return { previous }
        },
        onError: (_err, { id }, context: any) => {
            // Roll back on error
            if (context?.previous) {
                queryClient.setQueryData(['collection', id], context.previous)
            }
        },
        onSuccess: (updatedCollection: CollectionTypes.CollectionDto) => {
            // Normalize the updated collection and store in cache
            const normalizedCollection = normalizeCollection(
                queryClient,
                updatedCollection
            )
            queryClient.setQueryData(
                ['collection', updatedCollection.id],
                normalizedCollection
            )

            // Automatically set updated collection as current (editing implies active interaction)
            if (
                isAuthenticated &&
                user &&
                updatedCollection.ownerId === user.uid
            ) {
                setCurrentCollection(updatedCollection.id)
            }

            // Invalidate the user's profile so their collections list (including name/privacy) is refreshed
            queryClient.invalidateQueries({
                queryKey: ['user', 'profile', updatedCollection.ownerId],
            })
        },
        onSettled: (_data, _error, { id }) => {
            // Optionally ensure queries are in sync
            queryClient.invalidateQueries({ queryKey: ['collection', id] })
        },
    })
}

/**
 * Hook to delete a collection
 */
export function useDeleteCollection() {
    const queryClient = useQueryClient()
    const { getCurrentCollection, clearCurrentCollection } =
        useCurrentCollectionStore()
    const { user, isAuthenticated } = useCurrentUser()

    return useMutation({
        mutationFn: (id: string) => collectionsApi.deleteCollection(id),
        onSuccess: (_: void, id: string) => {
            // Clear current collection if user deletes the currently selected collection
            if (isAuthenticated && user && getCurrentCollection() === id) {
                clearCurrentCollection()
            }

            queryClient.invalidateQueries({ queryKey: ['collections'] })
            queryClient.removeQueries({ queryKey: ['collection', id] })
        },
    })
}

/**
 * Hook to archive a collection
 */
export function useArchiveCollection() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => collectionsApi.archiveCollection(id),
        onSuccess: (updatedCollection: CollectionTypes.CollectionDto) => {
            // Update the collection in cache
            const normalizedCollection = normalizeCollection(
                queryClient,
                updatedCollection
            )
            queryClient.setQueryData(
                ['collection', updatedCollection.id],
                normalizedCollection
            )
        },
    })
}

/**
 * Hook to unarchive a collection
 */
export function useUnarchiveCollection() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => collectionsApi.unarchiveCollection(id),
        onSuccess: (updatedCollection: CollectionTypes.CollectionDto) => {
            // Update the collection in cache
            const normalizedCollection = normalizeCollection(
                queryClient,
                updatedCollection
            )
            queryClient.setQueryData(
                ['collection', updatedCollection.id],
                normalizedCollection
            )
        },
    })
}

/**
 * Hook to add a product to a collection
 */
export function useAddProductToCollection() {
    const queryClient = useQueryClient()
    const { setCurrentCollection } = useCurrentCollectionStore()
    const { user, isAuthenticated } = useCurrentUser()
    const posthog = usePostHog()

    return useMutation({
        mutationFn: ({
            collectionId,
            data,
        }: {
            collectionId: string
            data: CollectionTypes.AddProductToCollectionRequest
        }) => collectionsApi.addProductToCollection(collectionId, data),
        onMutate: async ({
            collectionId,
            data,
        }: {
            collectionId: string
            data: { productId: string }
        }) => {
            // Cancel any outgoing refetches for this collection
            await queryClient.cancelQueries({
                queryKey: ['collection', collectionId],
            })
            // Snapshot previous collection data
            const previousCollection =
                queryClient.getQueryData<NormalizedCollection>([
                    'collection',
                    collectionId,
                ])
            // Optimistically add the productId
            if (
                previousCollection &&
                !previousCollection.productIds.includes(data.productId)
            ) {
                queryClient.setQueryData<NormalizedCollection>(
                    ['collection', collectionId],
                    {
                        ...previousCollection,
                        productIds: [
                            ...previousCollection.productIds,
                            data.productId,
                        ],
                    }
                )
            }
            return { previousCollection }
        },
        onError: (
            _err,
            { collectionId }: { collectionId: string },
            context: any
        ) => {
            // Rollback on error
            if (context?.previousCollection) {
                queryClient.setQueryData<NormalizedCollection>(
                    ['collection', collectionId],
                    context.previousCollection
                )
            }
        },
        onSuccess: async (_: void, { collectionId, data }: any) => {
            // Track product saved to collection
            posthog?.capture(AnalyticsEvents.PRODUCT_SAVED, {
                product_id: data.productId,
                collection_id: collectionId,
            })

            // Get collection data to check ownership
            const collection = queryClient.getQueryData<NormalizedCollection>([
                'collection',
                collectionId,
            ])

            // Automatically set collection as current when user adds products to their own collection
            if (
                isAuthenticated &&
                user &&
                collection &&
                collection.ownerId === user.uid
            ) {
                setCurrentCollection(collectionId)
            }

            // Invalidate queries to refetch fresh data
            queryClient.invalidateQueries({
                queryKey: ['collection', collectionId],
            })
            queryClient.invalidateQueries({
                queryKey: ['product', data.productId],
            })
        },
    })
}

/**
 * Hook to remove a product from a collection
 */
export function useRemoveProductFromCollection() {
    const queryClient = useQueryClient()
    const { setCurrentCollection } = useCurrentCollectionStore()
    const { user, isAuthenticated } = useCurrentUser()
    const posthog = usePostHog()

    return useMutation({
        mutationFn: ({
            collectionId,
            productId,
        }: {
            collectionId: string
            productId: string
        }) =>
            collectionsApi.removeProductFromCollection(collectionId, productId),
        onMutate: async ({
            collectionId,
            productId,
        }: {
            collectionId: string
            productId: string
        }) => {
            // Cancel any outgoing refetches for this collection
            await queryClient.cancelQueries({
                queryKey: ['collection', collectionId],
            })
            // Snapshot previous collection data
            const previousCollection =
                queryClient.getQueryData<NormalizedCollection>([
                    'collection',
                    collectionId,
                ])
            // Optimistically remove the productId
            if (
                previousCollection &&
                previousCollection.productIds.includes(productId)
            ) {
                queryClient.setQueryData<NormalizedCollection>(
                    ['collection', collectionId],
                    {
                        ...previousCollection,
                        productIds: previousCollection.productIds.filter(
                            (id) => id !== productId
                        ),
                    }
                )
            }
            return { previousCollection }
        },
        onError: (
            _err,
            { collectionId }: { collectionId: string },
            context: any
        ) => {
            // Rollback on error
            if (context?.previousCollection) {
                queryClient.setQueryData<NormalizedCollection>(
                    ['collection', collectionId],
                    context.previousCollection
                )
            }
        },
        onSuccess: (_: void, { collectionId, productId }: any) => {
            // Track product removed from collection
            posthog?.capture(AnalyticsEvents.PRODUCT_UNSAVED, {
                product_id: productId,
                collection_id: collectionId,
            })

            // Get collection data to check ownership
            const collection = queryClient.getQueryData<NormalizedCollection>([
                'collection',
                collectionId,
            ])

            // Automatically set collection as current when user removes products from their own collection
            if (
                isAuthenticated &&
                user &&
                collection &&
                collection.ownerId === user.uid
            ) {
                setCurrentCollection(collectionId)
            }

            // Invalidate queries to refetch fresh data
            queryClient.invalidateQueries({
                queryKey: ['collection', collectionId],
            })
            queryClient.invalidateQueries({ queryKey: ['product', productId] })
        },
    })
}

/**
 * Hook to add a look to a collection
 */
export function useAddLookToCollection(currentUserId?: string) {
    const queryClient = useQueryClient()
    const { setCurrentCollection } = useCurrentCollectionStore()
    const { user, isAuthenticated } = useCurrentUser()

    return useMutation({
        mutationFn: ({
            collectionId,
            lookId,
        }: {
            collectionId: string
            lookId: string
        }) => collectionsApi.addLookToCollection(collectionId, { lookId }),
        onMutate: async ({
            collectionId,
            lookId,
        }: {
            collectionId: string
            lookId: string
        }) => {
            await queryClient.cancelQueries({
                queryKey: ['collection', collectionId],
            })
            const previousCollection =
                queryClient.getQueryData<NormalizedCollection>([
                    'collection',
                    collectionId,
                ])
            // Optimistically add lookId to lookIds if not already present
            if (
                previousCollection &&
                !previousCollection.lookIds.includes(lookId)
            ) {
                queryClient.setQueryData(['collection', collectionId], {
                    ...previousCollection,
                    lookIds: [...previousCollection.lookIds, lookId],
                })
            }
            return { previousCollection }
        },
        onError: (
            _err,
            { collectionId }: { collectionId: string; lookId: string },
            context: any
        ) => {
            if (context?.previousCollection) {
                queryClient.setQueryData(
                    ['collection', collectionId],
                    context.previousCollection
                )
            }
        },
        onSuccess: async (
            _: void,
            { collectionId }: { collectionId: string; lookId: string }
        ) => {
            // Get collection data to check ownership
            const collection = queryClient.getQueryData<NormalizedCollection>([
                'collection',
                collectionId,
            ])

            // Automatically set collection as current when user adds looks to their own collection
            if (
                isAuthenticated &&
                user &&
                collection &&
                collection.ownerId === user.uid
            ) {
                setCurrentCollection(collectionId)
            }

            // Invalidate and refetch the collection to ensure fresh data from server
            await queryClient.invalidateQueries({
                queryKey: ['collection', collectionId],
            })
            if (currentUserId) {
                await queryClient.invalidateQueries({
                    queryKey: ['user', currentUserId, 'collections'],
                    exact: false,
                })
            }
        },
    })
}

/**
 * Hook to remove a look from a collection
 */
export function useRemoveLookFromCollection(currentUserId?: string) {
    const queryClient = useQueryClient()
    const { setCurrentCollection } = useCurrentCollectionStore()
    const { user, isAuthenticated } = useCurrentUser()

    return useMutation({
        mutationFn: ({
            collectionId,
            lookId,
        }: {
            collectionId: string
            lookId: string
        }) => collectionsApi.removeLookFromCollection(collectionId, lookId),
        onMutate: async ({
            collectionId,
            lookId,
        }: {
            collectionId: string
            lookId: string
        }) => {
            await queryClient.cancelQueries({
                queryKey: ['collection', collectionId],
            })
            const previousCollection =
                queryClient.getQueryData<NormalizedCollection>([
                    'collection',
                    collectionId,
                ])
            // Optimistically remove lookId from lookIds
            if (
                previousCollection &&
                previousCollection.lookIds.includes(lookId)
            ) {
                queryClient.setQueryData(['collection', collectionId], {
                    ...previousCollection,
                    lookIds: previousCollection.lookIds.filter(
                        (id) => id !== lookId
                    ),
                })
            }
            return { previousCollection }
        },
        onError: (
            _err,
            { collectionId }: { collectionId: string; lookId: string },
            context: any
        ) => {
            if (context?.previousCollection) {
                queryClient.setQueryData(
                    ['collection', collectionId],
                    context.previousCollection
                )
            }
        },
        onSuccess: async (
            _: void,
            { collectionId }: { collectionId: string; lookId: string }
        ) => {
            // Get collection data to check ownership
            const collection = queryClient.getQueryData<NormalizedCollection>([
                'collection',
                collectionId,
            ])

            // Automatically set collection as current when user removes looks from their own collection
            if (
                isAuthenticated &&
                user &&
                collection &&
                collection.ownerId === user.uid
            ) {
                setCurrentCollection(collectionId)
            }

            // Invalidate and refetch the collection to ensure fresh data from server
            await queryClient.invalidateQueries({
                queryKey: ['collection', collectionId],
            })
            if (currentUserId) {
                await queryClient.invalidateQueries({
                    queryKey: ['user', currentUserId, 'collections'],
                    exact: false,
                })
            }
        },
    })
}

/**
 * Hook to save (bookmark) a collection
 */
export function useSaveCollection(currentUserId?: string) {
    const queryClient = useQueryClient()
    const { setCurrentCollection } = useCurrentCollectionStore()
    const { user, isAuthenticated } = useCurrentUser()
    const posthog = usePostHog()

    return useMutation({
        mutationFn: (id: string) => collectionsApi.saveCollection(id),
        // Optimistically update collection and current user's profile
        onMutate: async (id: string) => {
            // Cancel ongoing queries for collection and user profile
            await queryClient.cancelQueries({ queryKey: ['collection', id] })
            if (currentUserId)
                await queryClient.cancelQueries({
                    queryKey: ['user', 'profile', currentUserId],
                })
            // snapshot
            const previousCollection =
                queryClient.getQueryData<NormalizedCollection>([
                    'collection',
                    id,
                ])
            const previousProfile = currentUserId
                ? queryClient.getQueryData<NormalizedProfileDto>([
                      'user',
                      'profile',
                      currentUserId,
                  ])
                : undefined
            // optimistic collection
            if (previousCollection) {
                queryClient.setQueryData<NormalizedCollection>(
                    ['collection', id],
                    { ...previousCollection, isSaved: true }
                )
            }

            // Optimistically update user's saved list
            if (currentUserId && previousProfile) {
                queryClient.setQueryData<NormalizedProfileDto>(
                    ['user', 'profile', currentUserId],
                    {
                        ...previousProfile,
                        privateAndSavedCollectionIds: Array.from(
                            new Set([
                                ...previousProfile.privateAndSavedCollectionIds,
                                id,
                            ])
                        ),
                    }
                )
            }
            return { previousCollection, previousProfile }
        },
        onError: (_error, id, context) => {
            // Rollback collection
            if (context?.previousCollection) {
                queryClient.setQueryData<NormalizedCollection>(
                    ['collection', id],
                    context.previousCollection
                )
            }
            // Rollback user profile
            if (currentUserId && context?.previousProfile) {
                queryClient.setQueryData<NormalizedProfileDto>(
                    ['user', 'profile', currentUserId],
                    context.previousProfile
                )
            }
        },
        // After success or error, refetch to sync
        onSuccess: (updated: CollectionTypes.CollectionDto) => {
            // Track collection saved
            posthog?.capture(AnalyticsEvents.COLLECTION_SAVED, {
                collection_id: updated.id,
                owner_id: updated.ownerId,
            })

            // apply server response
            const normalized = normalizeCollection(queryClient, updated)
            queryClient.setQueryData(['collection', updated.id], normalized)

            // Automatically set saved collection as current when user saves it
            // This shows intent to work with this collection
            if (isAuthenticated && user) {
                setCurrentCollection(updated.id)
            }

            // update profile accordingly
            if (currentUserId) {
                queryClient.setQueryData<NormalizedProfileDto>(
                    ['user', 'profile', currentUserId],
                    (prev) => {
                        if (!prev) return prev
                        return {
                            ...prev,
                            privateAndSavedCollectionIds: Array.from(
                                new Set([
                                    ...(prev.privateAndSavedCollectionIds ||
                                        []),
                                    updated.id,
                                ])
                            ),
                        }
                    }
                )
            }
        },
    })
}

/**
 * Hook to unsave (remove bookmark) a collection
 */
export function useUnsaveCollection(currentUserId?: string) {
    const queryClient = useQueryClient()
    const { getCurrentCollection, clearCurrentCollection } =
        useCurrentCollectionStore()
    const { user, isAuthenticated } = useCurrentUser()
    const posthog = usePostHog()

    return useMutation({
        mutationFn: (id: string) => collectionsApi.unsaveCollection(id),
        // Optimistically update collection and current user's profile
        onMutate: async (id: string) => {
            await queryClient.cancelQueries({ queryKey: ['collection', id] })
            if (currentUserId)
                await queryClient.cancelQueries({
                    queryKey: ['user', 'profile', currentUserId],
                })
            const previousCollection =
                queryClient.getQueryData<NormalizedCollection>([
                    'collection',
                    id,
                ])
            const previousProfile = currentUserId
                ? queryClient.getQueryData<NormalizedProfileDto>([
                      'user',
                      'profile',
                      currentUserId,
                  ])
                : undefined
            if (previousCollection) {
                queryClient.setQueryData<NormalizedCollection>(
                    ['collection', id],
                    { ...previousCollection, isSaved: false }
                )
            }
            if (currentUserId && previousProfile) {
                queryClient.setQueryData<NormalizedProfileDto>(
                    ['user', 'profile', currentUserId],
                    {
                        ...previousProfile,
                        privateAndSavedCollectionIds:
                            previousProfile.privateAndSavedCollectionIds.filter(
                                (cid) => cid !== id
                            ),
                    }
                )
            }
            return { previousCollection, previousProfile }
        },
        onError: (_error, id, context) => {
            // Rollback collection
            if (context?.previousCollection) {
                queryClient.setQueryData<NormalizedCollection>(
                    ['collection', id],
                    context.previousCollection
                )
            }
            // Rollback user profile
            if (currentUserId && context?.previousProfile) {
                queryClient.setQueryData<NormalizedProfileDto>(
                    ['user', 'profile', currentUserId],
                    context.previousProfile
                )
            }
        },
        onSuccess: (updated: CollectionTypes.CollectionDto) => {
            // Track collection unsaved
            posthog?.capture(AnalyticsEvents.COLLECTION_UNSAVED, {
                collection_id: updated.id,
                owner_id: updated.ownerId,
            })

            const normalized = normalizeCollection(queryClient, updated)
            queryClient.setQueryData(['collection', updated.id], normalized)

            // Clear current collection if user unsaves the currently selected collection
            // This prevents them from saving to a collection they no longer have access to
            if (
                isAuthenticated &&
                user &&
                getCurrentCollection() === updated.id
            ) {
                clearCurrentCollection()
            }

            if (currentUserId) {
                queryClient.setQueryData<NormalizedProfileDto>(
                    ['user', 'profile', currentUserId],
                    (prev) => {
                        if (!prev) return prev
                        return {
                            ...prev,
                            privateAndSavedCollectionIds:
                                prev.privateAndSavedCollectionIds.filter(
                                    (cid) => cid !== updated.id
                                ),
                        }
                    }
                )
            }
        },
    })
}
